import { mainApi } from '../index';
import { getLocalTimezone } from '@/lib';
import type {
    AddContactPayload,
    CustomersQueryParams,
    CustomersResponse,
} from '@/features/customers/types/customers.types';
import type {
    AddToEmailFlowPayload,
    BulkAffiliatePayload,
    BulkDeletePayload,
    BulkProductPayload,
    BulkTagPayload,
    BulkUserFanIdsResponse,
} from './types';

export async function fetchCustomers(params: CustomersQueryParams, signal?: AbortSignal): Promise<CustomersResponse> {
    const response = await mainApi.get<CustomersResponse>('v1/orders/audience', {
        params: {
            page: params.page,
            pageSize: params.pageSize,
            filter_array: params.filter_array,
            timezone: params.timezone || getLocalTimezone(),
        },
        signal,
    });
    return response.data;
}

export async function addContact(payload: AddContactPayload): Promise<void> {
    await mainApi.post('v1/users/fans', {
        full_name: payload.name,
        email: payload.email,
        phone_number: payload.phoneNumber || '',
        user_fan: '',
    });
}

export async function importContactsCSV(file: File, tag?: string): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    await mainApi.post('v1/users/fans/import-csv', formData, {
        params: tag ? { tag } : undefined,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

// ============================================
// Bulk Operations APIs
// ============================================

/**
 * Fetch all user fan IDs matching the current filter criteria
 * Used as a first step before bulk operations
 */
export async function fetchBulkUserFanIds(filterArray: string): Promise<number[]> {
    const response = await mainApi.get<BulkUserFanIdsResponse>('v1/users/fans/bulk/user_fan_ids', {
        params: {
            page: '1',
            timezone: getLocalTimezone(),
            filter_array: filterArray,
        },
    });
    return response.data.user_fan_ids;
}

/**
 * Add tags to multiple customers in bulk
 */
export async function bulkAddTag(payload: BulkTagPayload): Promise<void> {
    await mainApi.put('v1/users/fans/tags/bulk', payload);
}

/**
 * Remove tags from multiple customers in bulk
 */
export async function bulkRemoveTag(payload: BulkTagPayload): Promise<void> {
    await mainApi.delete('v1/users/fans/tags/bulk', { data: payload });
}

/**
 * Grant product access to multiple customers in bulk
 */
export async function bulkGrantProductAccess(payload: BulkProductPayload): Promise<void> {
    await mainApi.put('v1/users/fans/product/bulk', payload);
}

/**
 * Grant affiliate access to multiple customers in bulk
 */
export async function bulkGrantAffiliateAccess(payload: BulkAffiliatePayload): Promise<void> {
    await mainApi.put('v1/users/fans/product/affiliate-access/bulk', payload);
}

/**
 * Add customers to an email flow
 */
export async function addToEmailFlow(flowId: number, payload: AddToEmailFlowPayload): Promise<void> {
    await mainApi.post(`v1/flows/${flowId}/recipient-list/add`, payload);
}

/**
 * Delete multiple customers in bulk
 */
export async function bulkDeleteCustomers(payload: BulkDeletePayload): Promise<{ processed?: number }> {
    const response = await mainApi.delete('v1/users/fans/bulk', { data: payload });
    return response.data;
}
